/**
 * QA Recommend — API จริง (คำตอบที่แนะนำทุก X ช่วงสนทนา)
 *
 * ⚠️ โมดูลนี้ **ไม่มีโหมดจำลอง** โดยตั้งใจ — และตั้งแต่ Q10 ทั้ง QA Console ก็ไม่มีแล้ว
 *
 *    ฟีเจอร์นี้มี backend จริงตั้งแต่วันแรก และค่าที่ตั้งในหน้าจอ **ไปสั่งงานเบื้องหลังจริง**
 *    (ยิง LLM ซึ่งมีค่าใช้จ่ายต่อรอบ) · ถ้าทำโหมดจำลองให้ด้วย จะเกิดกรณีที่คนกดบันทึก
 *    แล้วเห็นข้อความว่าสำเร็จ ทั้งที่ไม่มีอะไรถูกบันทึกจริง แล้วไปสรุปว่าฟีเจอร์เสีย
 *
 *    ผลที่ตามมาซึ่งต้องรู้: env ที่ backend ยังไม่พร้อม แท็บนี้จะขึ้น error ตรง ๆ
 *    ซึ่งเป็นสิ่งที่ต้องการ — บอกความจริงว่าต่อไม่ติด ดีกว่าแสร้งว่าใช้ได้
 *
 * แผนงาน: docs/qa-console/Plan-QA-Recomend.md §10
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';
const ROOT = 'api/v1/QaConsole';
const API_STATUS_SUCCESS = 0; // APIStatus.Successful

const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

/**
 * ดัก "route ไม่มีอยู่จริงบน API ตัวที่คุยด้วย"
 *
 * ⚠️ ฝั่ง server ทำ SPA fallback ไว้ (MapFallbackToFile) · path ที่ไม่ตรง route ไหนเลย
 *    จึงได้ **HTTP 200 + index.html** กลับมา ไม่ใช่ 404 — axios ถือว่าสำเร็จ แล้ว unwrap
 *    เดิมคืน HTML string ออกไปเป็น "ข้อมูล" เพราะมันไม่ใช่ object จึงไม่เข้าเงื่อนไขไหนเลย
 *
 *    ผลที่ตามมาคือหน้าจอพังตอน render (`data.rounds.length` ของ HTML string = TypeError)
 *    ซึ่งทำให้ทั้งการ์ดผู้ช่วย AI และ sentiment ที่อยู่ถัดไปหายทั้งคู่ — อาการดูเหมือน
 *    "หน้าค้าง" ทั้งที่สาเหตุจริงคือ API ที่ deploy อยู่เป็น build เก่าที่ยังไม่มี endpoint นี้
 *    (เจอจริง 20 ส.ค. 2026 บน dev-chat-lhb-app)
 *
 *    จึงต้องแปลงเป็น error ตั้งแต่ตรงนี้ ให้ผู้เรียกเห็นว่า "ต่อไม่ติด" ตามเจตนาที่หัวไฟล์
 *    เขียนไว้ ไม่ใช่ปล่อยของที่ไม่ใช่ข้อมูลไหลลงไปถึง component
 */
function assertNotSpaFallback(response) {
  const headers = (response && response.headers) || {};
  const contentType = String(headers['content-type'] || headers['Content-Type'] || '');
  const body = response && response.data;
  const looksLikeHtml =
    contentType.includes('text/html') || (typeof body === 'string' && body.trimStart().startsWith('<'));
  if (!looksLikeHtml) return;

  const err = new Error(
    'API ตอบหน้าเว็บแทนข้อมูล — endpoint นี้ไม่มีอยู่บน API ที่กำลังคุยด้วย ' +
      '(ปกติแปลว่า build ที่ deploy อยู่เก่ากว่าฟีเจอร์นี้)'
  );
  err.httpStatus = response && response.status;
  err.isRouteMissing = true;
  throw err;
}

function unwrap(response) {
  assertNotSpaFallback(response);
  const body = response && response.data;
  if (body && typeof body === 'object' && 'status' in body) {
    if (body.status === API_STATUS_SUCCESS) return body.data;
    const err = new Error(body.message || 'Request failed');
    err.status = body.status;
    throw err;
  }
  return body;
}

function toError(error) {
  // ⚠️ error ที่เราสร้างเองไม่มี `response` — ถ้าปล่อยให้ห่อซ้ำ ธง isRouteMissing กับ
  //    httpStatus จะหายไป แล้วผู้เรียกแยกไม่ออกว่า "ไม่มี route" ต่างจาก "ยิงไม่ถึง server"
  if (error && error.isRouteMissing) return error;

  const body = error && error.response && error.response.data;
  const err = new Error(
    (body && (body.message || body.Message)) || (error && error.message) || 'Request failed'
  );
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw toError(e);
  }
}

async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    throw toError(e);
  }
}

/** ค่าตั้งที่ใช้อยู่ของแท็บ "คำแนะนำ AI" */
export function getRecommendSetting() {
  return get(`${ROOT}/recommend/setting`);
}

/**
 * รายการ LLM ให้เลือกในหน้าค่าตั้ง — [{ id, name }] จากทะเบียนเดียวกับหน้า LLMs Management
 *
 * ⚠️ สิทธิ์แคบเท่ากับการบันทึกค่าตั้ง (role 0,1) — ผู้เรียกต้องรับ 403 ได้
 */
export function getRecommendModels() {
  return get(`${ROOT}/recommend/models`);
}

/**
 * บันทึกค่าตั้ง — ทับของเดิม ไม่มีเลขเวอร์ชันคืนกลับมาแล้ว
 *
 * ⚠️ path มี /save ต่อท้ายเพราะทะเบียนสิทธิ์ฝั่ง server แยกตาม path ไม่ดู HTTP method
 *    (การเขียนจำกัดไว้ที่ role 0,1 ส่วนการอ่านเปิดให้ 0,1,2)
 */
export function saveRecommendSetting(setting) {
  return post(`${ROOT}/recommend/setting/save`, setting);
}

/** คำแนะนำของเคส แยกตามรอบ · caseKey รับได้ทั้ง conversationId และ endUserKey */
export function getCaseRecommend(caseKey) {
  return get(`${ROOT}/cases/${encodeURIComponent(caseKey)}/recommend`);
}

/** เจ้าหน้าที่กด "ใช้" / "ไม่ใช้" — ทางเดียวที่หน้าจอเขียนข้อมูลกลับ */
export function setSuggestionUsed(suggestionId, usedStatus) {
  return post(`${ROOT}/recommend/suggestions/${encodeURIComponent(suggestionId)}/used`, {
    usedStatus,
  });
}

/** สถานะคิวเบื้องหลัง (role 0,1 เท่านั้น — ผู้เรียกต้องรับ 403 ได้) */
export function getRecommendQueue() {
  return get(`${ROOT}/recommend/queue`);
}

/** ปิดบทสนทนาด้วยมือ — ใช้ทดสอบว่ารอบที่ยังไม่ครบถูกทิ้งจริง */
export function endRecommendConversation(endUserKey) {
  return post(`${ROOT}/recommend/end-conversation`, { endUserKey });
}
